// @ts-ignore

import { type Context, type EventOverrides } from '../types';

import { type SegmentContextDef } from './types';
import { nestedRemoveUndefinedValues } from './util';

// Shallow removal of all falsey properties from an object, returning a new object.
const removeFalseyProperties = <T extends object>(obj: T): { [K in keyof T]: T[K] } => {
	const result = {} as any;

	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			// If the value is not strictly true, map it to undefined
			if (obj[key]) {
				result[key] = obj[key];
			}
		}
	}

	return result as { [K in keyof T]: T[K] };
};

export const buildContext = (context: Context, overrides?: EventOverrides): SegmentContextDef => {
	const result = {
		...context.context,
		userAgent: navigator.userAgent,
	};

	// use nestedRemoveUndefinedValues to strip undefined properties before
	// spreading the override object since we want properties that explicitly
	// set to 'undefined' to not overwrite any values, just like properties
	// that are 'undefined' because they're not set at all will.
	if (overrides?.excludedProperties) {
		result.excludedProperties = {
			...result.excludedProperties,
			...nestedRemoveUndefinedValues(overrides.excludedProperties, '$.excludedProperties'),
		};
	}
	if (overrides?.userConsents) {
		result.userConsents = {
			...result.userConsents,
			...nestedRemoveUndefinedValues(overrides.userConsents, '$.userConsents'),
		};
	}

	// Remove any falsey properties since we only want to have exclusions
	// that were explicitly declared as excluded.
	if (result.excludedProperties) {
		result.excludedProperties = removeFalseyProperties(result.excludedProperties);
	}

	return result;
};
